import { useState } from 'react';
import { CheckIcon, ChevronDownIcon, GlobeIcon, GlobeOffIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type ChatWebSearchToggleProps = {
  disabled?: boolean;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
};

const options = [
  {
    enabled: true,
    label: 'Actif',
  },
  {
    enabled: false,
    label: 'Inactif',
  },
] as const;

export function ChatWebSearchToggle({
  disabled = false,
  enabled,
  onChange,
}: ChatWebSearchToggleProps) {
  const [open, setOpen] = useState(false);

  function handleSelect(nextValue: boolean) {
    if (disabled || nextValue === enabled) {
      setOpen(false);
      return;
    }

    onChange(nextValue);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          data-testid="chat-web-search-toggle"
          data-web-search-enabled={enabled ? 'true' : 'false'}
          aria-label={enabled ? 'Recherche web activée' : 'Recherche web désactivée'}
          className="h-9 gap-1.5 rounded-full px-3 text-sm text-foreground/70 hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.06]"
        >
          {enabled ? (
            <GlobeIcon className="size-[1.1rem] shrink-0 stroke-[2.2]" aria-hidden />
          ) : (
            <GlobeOffIcon className="size-[1.1rem] shrink-0 stroke-[2.2]" aria-hidden />
          )}
          <span className="shrink-0 text-sm font-medium leading-none">Web</span>
          <ChevronDownIcon className="size-3.5 shrink-0 text-muted-foreground/80" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-52 p-1">
        <p className="px-2.5 py-2 text-sm font-medium text-foreground">Recherche sur le web</p>
        <div className="flex flex-col gap-0.5 border-t border-border/60 pt-1">
          {options.map((option) => {
            const isSelected = enabled === option.enabled;

            return (
              <button
                key={option.label}
                type="button"
                data-testid={option.enabled ? 'chat-web-search-enable' : 'chat-web-search-disable'}
                onClick={() => handleSelect(option.enabled)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
                  isSelected
                    ? 'bg-muted/70 text-foreground'
                    : 'text-foreground hover:bg-muted/50',
                )}
              >
                <CheckIcon
                  className={cn('size-3.5 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                  aria-hidden
                />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
